// A nav row, not a banner, so it reuses AsideBarLink but keeps useAppPlacement's gating and tracking

import React, { useCallback, useEffect, useState } from "react";
import AsideBarLink from "../AsidebarLink";
import AppInstallBanner from "../../../../components/AppInstallBanner/AppInstallBanner";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import useAppPlacement from "../../../../customHooks/useAppPlacement";
import usePrefersQr from "../../../../customHooks/usePrefersQr";
import { APP_PLACEMENTS, isAppPlacementCountry } from "../../../../utils/appPlacements";
import * as translator from "../../../../utils/translator";

/** Figma's pill gradient; without it BadgeNew falls back to its default red. */
const BADGE_GRADIENT = "linear-gradient(90deg, #269C6E 0%, #7ABA47 100%)";

/** Must match the @media (max-width: 1200px) accordion block in MyAccountAsideBar.css. */
const ACCORDION_QUERY = "(max-width: 1200px)";

const useIsAccordion = (): boolean => {
  const [isAccordion, setIsAccordion] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(ACCORDION_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(ACCORDION_QUERY);
    const onChange = () => setIsAccordion(mql.matches);
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);
  return Boolean(isAccordion);
};

const W6 = APP_PLACEMENTS.W6;

/** W3's art suits the whole account; the other banners picture one order or plan. */
const POPUP_DESIGN = APP_PLACEMENTS.W3;

export interface AppInstallAsideBarLinkProps {
  language: string;
  country?: string | null;
  customerUuid?: string | null;
  /** Undefined while the asidebar response is still loading. */
  isPairedToMobileApp?: boolean;
  /** Placement ids the api allows. Undefined while loading, which keeps the row hidden. */
  allowedPlacements?: readonly string[];
  activeMenuLinkId: string;
  /** Accordion open state. Below 1200px the sidebar is collapsed by default. */
  showAllLinks?: boolean;
}

const AppInstallAsideBarLink = ({
  language,
  country,
  customerUuid,
  isPairedToMobileApp,
  allowedPlacements,
  activeMenuLinkId,
  showAllLinks = true,
}: AppInstallAsideBarLinkProps) => {
  const isAccordion = useIsAccordion();
  // A computer cannot open the app link, so it gets the banner's QR in a popup; an unknown device counts as one
  const isComputer = usePrefersQr() ?? true;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const closePopup = useCallback(() => setIsPopupOpen(false), []);
  const t = (key: string) =>
    translator.translate(language, W6.translationCategory, `${W6.translationKeyPrefix}/${key}`);
  const { visible, href, onClick, ref } = useAppPlacement({
    placementId: "W6",
    country,
    customerUuid,
    isPairedToMobileApp,
    allowedPlacements,
    // A collapsed accordion still renders the row, so the impression waits until it opens.
    inView: !isAccordion || showAllLinks,
    // Only de has the copy, and a missing key logs a translator error on every render elsewhere
    ctaText: isAppPlacementCountry(country) ? t("row_label") : "",
  });

  if (!visible) return null;

  const onRowClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick();
    if (!isComputer) return;
    e.preventDefault();
    setIsPopupOpen(true);
  };

  return (
    <>
      <AsideBarLink
        itemRef={ref}
        id="app-install"
        activeMenuLinkId={activeMenuLinkId}
        linkText={t("row_label")}
        // A new tab on a computer would land on AppsFlyer's desktop fallback, so the row points at itself
        href={isComputer ? "#app-install" : href}
        onClick={onRowClick}
        iconSrc={require("../../../../icons/App-Install-Secondary.svg").default}
        alt="app-install"
        forceShowIcon
        showBadgeNew
        badgeNewText={t("badge")}
        badgeNewColor={BADGE_GRADIENT}
      />
      {isPopupOpen && (
        <AppInstallPopup
          language={language}
          country={country}
          customerUuid={customerUuid}
          isPairedToMobileApp={isPairedToMobileApp}
          allowedPlacements={allowedPlacements}
          onClose={closePopup}
        />
      )}
    </>
  );
};

interface AppInstallPopupProps {
  language: string;
  country?: string | null;
  customerUuid?: string | null;
  isPairedToMobileApp?: boolean;
  allowedPlacements?: readonly string[];
  onClose: () => void;
}

const AppInstallPopup = ({ language, onClose, ...placementProps }: AppInstallPopupProps) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <PortaledModal shouldShowModal hideModal={onClose}>
      <div
        className="app-install-popup"
        role="dialog"
        aria-modal="true"
        aria-label={translator.translate(language, W6.translationCategory, `${W6.translationKeyPrefix}/headline`)}
      >
        <button
          type="button"
          className="app-install-popup__close"
          aria-label={translator.translate(language, "default", "AndroidAppBar/close")}
          onClick={onClose}
        >
          ×
        </button>
        <AppInstallBanner
          desktopPlacementId="W6"
          mobilePlacementId="W6"
          designPlacementId={POPUP_DESIGN.id}
          countView={false}
          language={language}
          {...placementProps}
        />
      </div>
    </PortaledModal>
  );
};

export default AppInstallAsideBarLink;
